<template>
  <div class="loginVue">
    <div class="login-wrap privacy-review-wrap">
      <div class="container">
        <div v-loading="loading" class="privacy-review-box">

          <!-- No pending policies — auto-redirect -->
          <div v-if="!loading && pendingPolicies.length === 0" class="text-center" style="padding: 40px; color: #A0CDEA;">
            <p>No pending policies. Redirecting...</p>
          </div>

          <!-- Show current policy -->
          <div v-if="currentPolicy">
            <div class="privacy-review-header">
              <h3>{{ currentPolicy.title }}</h3>
              <p class="privacy-review-counter" v-if="pendingPolicies.length > 1">
                Policy {{ currentIndex + 1 }} of {{ pendingPolicies.length }}
              </p>
            </div>

            <div class="privacy-review-content" ref="scrollContainer" @scroll="onScroll">
              <div v-html="safePolicyHtml"></div>
            </div>

            <div class="privacy-review-footer">
              <p v-if="!scrolledToBottom" class="scroll-hint">
                <i class="fa fa-arrow-down"></i> Please scroll to the bottom to continue
              </p>
              <button
                class="c-solid-btn c-solid-btn--green privacy-acknowledge-btn"
                :disabled="!scrolledToBottom || acknowledging"
                @click="acknowledgePolicy"
              >
                <i class="fa fa-check"></i> I Acknowledge
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import service from '../services';
import { mapGetters } from 'vuex';
import { sanitize } from '../safeHtmlRules.js';   // #2041

export default {
  name: 'PrivacyPolicyReview',
  data() {
    return {
      loading: true,
      pendingPolicies: [],
      currentIndex: 0,
      scrolledToBottom: false,
      acknowledging: false,
      intendedRoute: ''
    };
  },
  computed: {
      /**
       * #2041 — the policy is rendered with v-html and shown to every worker who has to
       * acknowledge it. Every policy stored before #2041 is unvalidated, so the DISPLAY path —
       * not the save path — is what disarms them. 'policy' profile: #2036's allow-list plus
       * headings.
       */
      safePolicyHtml() {
        return sanitize(this.currentPolicy && this.currentPolicy.content, 'policy');
      },

    ...mapGetters({
      userToken: 'userToken',
      userRole: 'userRole',
      userClientID: 'userClientID',
      employeeId: 'employeeId',
      userRoleSlug: 'userRoleSlug'
    }),
    currentPolicy() {
      return this.pendingPolicies[this.currentIndex] || null;
    }
  },
  methods: {
    async loadPendingPolicies() {
      this.loading = true;
      try {
        var res = await service.get('employee/pendingPrivacyPolicies', {
          'Authorization': this.userToken,
          'cs3-context-clientId': this.userClientID,
          'cs3-context-employeeId': this.employeeId,
          'cs3-context-role': this.userRole
        });
        this.pendingPolicies = res.data.result || [];

        if (this.pendingPolicies.length === 0) {
          this.navigateToDestination();
        }
      } catch (err) {
        console.log('loadPendingPolicies error:', err);
        // On error, don't block the user
        this.navigateToDestination();
      }
      this.loading = false;
    },

    onScroll() {
      var el = this.$refs.scrollContainer;
      if (!el) return;
      // Consider "scrolled to bottom" when within 20px of the bottom
      var atBottom = (el.scrollHeight - el.scrollTop - el.clientHeight) < 20;
      if (atBottom) {
        this.scrolledToBottom = true;
      }
    },

    checkAutoScroll() {
      // If content is short enough to not need scrolling, enable button immediately
      var self = this;
      self.$nextTick(function () {
        var el = self.$refs.scrollContainer;
        if (el && el.scrollHeight <= el.clientHeight) {
          self.scrolledToBottom = true;
        }
      });
    },

    async acknowledgePolicy() {
      if (!this.currentPolicy) return;
      this.acknowledging = true;
      try {
        await service.post('employee/acknowledgePrivacyPolicy/' + this.currentPolicy.id, {}, {
          'Authorization': this.userToken,
          'cs3-context-clientId': this.userClientID,
          'cs3-context-employeeId': this.employeeId,
          'cs3-context-role': this.userRole
        });

        // Move to next policy or navigate away
        if (this.currentIndex < this.pendingPolicies.length - 1) {
          this.currentIndex++;
          this.scrolledToBottom = false;
          this.$nextTick(function () {
            this.checkAutoScroll();
          }.bind(this));
        } else {
          this.navigateToDestination();
        }
      } catch (err) {
        console.log('acknowledgePolicy error:', err);
        this.$notify.error({ title: 'Error', message: 'Failed to acknowledge policy. Please try again.' });
      }
      this.acknowledging = false;
    },

    navigateToDestination() {
      var redirect = this.$route.query.redirect || '/jobList';
      if (redirect === '__mobile__') {
        window.location.href = window.location.href.split('#')[0] + 'mobile/';
      } else {
        this.$router.push({ path: redirect });
      }
    }
  },

  async created() {
    await this.loadPendingPolicies();
  },

  updated() {
    this.checkAutoScroll();
  }
};
</script>

<style scoped>
.privacy-review-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
}

.privacy-review-box {
  background: #335479;
  border-radius: 6px;
  max-width: 700px;
  width: 100%;
  margin: 30px auto;
  padding: 0;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.privacy-review-header {
  padding: 20px 25px 10px;
  border-bottom: 1px solid #466D98;
}

.privacy-review-header h3 {
  color: #fff;
  margin: 0 0 5px;
  font-size: 18px;
}

.privacy-review-counter {
  color: #A0CDEA;
  font-size: 13px;
  margin: 0;
}

.privacy-review-content {
  padding: 20px 25px;
  max-height: 50vh;
  overflow-y: auto;
  color: #fff;
  font-size: 14px;
  line-height: 1.6;
}

.privacy-review-content a {
  color: #5dade2;
}

.privacy-review-content::-webkit-scrollbar {
  width: 6px;
}

.privacy-review-content::-webkit-scrollbar-track {
  background: #234264;
}

.privacy-review-content::-webkit-scrollbar-thumb {
  background: #A0CDEA;
  border-radius: 3px;
}

.privacy-review-footer {
  padding: 15px 25px 20px;
  border-top: 1px solid #466D98;
  text-align: center;
}

.scroll-hint {
  color: #A0CDEA;
  font-size: 13px;
  margin-bottom: 10px;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.privacy-acknowledge-btn {
  padding: 10px 30px;
  font-size: 16px;
}

.privacy-acknowledge-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .privacy-review-box {
    margin: 10px;
    border-radius: 4px;
  }

  .privacy-review-content {
    max-height: 45vh;
    padding: 15px;
  }

  .privacy-review-header {
    padding: 15px;
  }

  .privacy-review-footer {
    padding: 15px;
  }
}
</style>
